"use client";

import React, { useState, useEffect, useRef } from "react";
import { useLocale } from "next-intl";
import { FilterGroup } from "@/data/filters";

// Component properties
type FilterBarProps = {
  filterGroups: FilterGroup[];
  onFilterChange: (activeFilters: Record<string, string | null>) => void;
  className?: string;
  initialFilters?: Record<string, string | null>;
  externalFilters?: Record<string, string | null>; // For external state control (Spotlight)
};

// Component for displaying filter tabs with separation
export default function FilterBar({ 
  filterGroups, 
  onFilterChange,
  className = "",
  initialFilters = {},
  externalFilters
}: FilterBarProps) {
  // Get current locale
  const locale = useLocale();
  
  // Initialize active filters with initial values or null for each group
  const [internalFilters, setInternalFilters] = useState<Record<string, string | null>>(() => {
    const defaultFilters = Object.fromEntries(filterGroups.map(group => [group.id, null]));
    return { ...defaultFilters, ...initialFilters };
  });

  // Use external filters if provided, otherwise use internal state
  const activeFilters = externalFilters || internalFilters;

  // Handle filter click - toggle active state
  const handleFilterClick = (groupId: string, tag: string) => {
    const newFilters = { 
      ...activeFilters,
      [groupId]: activeFilters[groupId] === tag ? null : tag
    };
    
    // Only update internal state if not using external control
    if (!externalFilters) {
      setInternalFilters(newFilters);
    }
    
    // Always call the change handler
    onFilterChange(newFilters);
  };

  // References for groups and container
  const containerRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Array<HTMLDivElement | null>>([]);
  const dividerRefs = useRef<Array<HTMLDivElement | null>>([]);
  
  // Function to check if groups are on different lines
  useEffect(() => {
    const checkLineBreaks = () => {
      if (!containerRef.current) return;
      
      // Reset refs array length to match current groups
      groupRefs.current = groupRefs.current.slice(0, filterGroups.length);
      dividerRefs.current = dividerRefs.current.slice(0, filterGroups.length);
      
      // Check each group's position
      groupRefs.current.forEach((group, index) => {
        if (index === 0 || !group) return; // Skip first group
        
        const prevGroup = groupRefs.current[index - 1];
        const divider = dividerRefs.current[index];
        
        if (!prevGroup || !divider) return;
        
        // Check if this group is on a new line
        const isNewLine = group.offsetTop > prevGroup.offsetTop;
        
        // Update divider style - horizontal line for new line, vertical for same line
        divider.classList.toggle('h-px', isNewLine);
        divider.classList.toggle('w-full', isNewLine);
        divider.classList.toggle('my-2', isNewLine);
        divider.classList.toggle('mx-0', isNewLine);
        
        divider.classList.toggle('h-5', !isNewLine);
        divider.classList.toggle('w-px', !isNewLine);
        divider.classList.toggle('mx-2', !isNewLine);
      });
    };
    
    // Run on mount and when window resizes
    checkLineBreaks();
    window.addEventListener('resize', checkLineBreaks);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkLineBreaks);
    };
  }, [filterGroups.length]);
  
  return (
    <div ref={containerRef} className={`flex flex-wrap items-start gap-2 ${className}`}>
      {filterGroups.map((group, groupIndex) => (
        <React.Fragment key={group.id}>
          {/* Adaptive divider between groups */}
          {groupIndex > 0 && (
            <div
              ref={el => { dividerRefs.current[groupIndex] = el; }}
              className="bg-muted-foreground/30 h-5 w-px mx-2"
            ></div>
          )}

          {/* Filter group */}
          <div
            ref={el => { groupRefs.current[groupIndex] = el; }}
            className="flex flex-wrap items-center gap-2"
          >
            {group.tags.map((tag, index) => {
              const isActive = activeFilters[group.id] === tag;
              const label = locale === 'pt' && group.labelsPt && group.labelsPt[index]
                ? group.labelsPt[index]
                : (group.labelsEn && group.labelsEn[index]
                  ? group.labelsEn[index]
                  : tag);
              const gradientMap: Record<string, string> = {
                'Unicorn': 'linear-gradient(to right, #A855F7, #EC4899)',
                'Exit': 'linear-gradient(to right, #10B981, #059669)',
              };
              const gradient = gradientMap[tag];

              if (gradient) {
                return (
                  <span
                    key={`${group.id}-${tag}`}
                    className="cursor-pointer rounded-full p-[1px] transition-all"
                    style={{ background: gradient }}
                    data-active={isActive}
                    onClick={() => handleFilterClick(group.id, tag)}
                  >
                    <span className={`
                      flex h-[26px] items-center justify-center whitespace-nowrap rounded-full px-4 text-center text-sm font-medium transition-all
                      ${isActive
                        ? 'bg-transparent text-white'
                        : 'bg-background text-muted-foreground hover:text-foreground'
                      }
                    `}>
                      {label}
                    </span>
                  </span>
                );
              }

              return (
              <span
                key={`${group.id}-${tag}`}
                className={`
                  cursor-pointer
                  flex h-7
                  items-center
                  justify-center
                  whitespace-nowrap
                  rounded-full
                  px-4
                  text-center
                  text-sm
                  font-medium
                  transition-all
                  border
                  ${isActive
                    ? 'bg-accent text-accent-foreground border-transparent'
                    : 'text-muted-foreground border-muted-foreground/30 hover:border-foreground hover:text-foreground'
                  }
                `}
                data-active={isActive}
                onClick={() => handleFilterClick(group.id, tag)}
              >
                {label}
              </span>
            );
            })}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}